/**
 * Provider List Item
 * Displays a single authentication provider with actions
 */

import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  Box,
  Typography,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Chip,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AuthProviderConfig, ProviderTemplate } from '../types';

interface ProviderListItemProps {
  provider: AuthProviderConfig;
  template: ProviderTemplate;
  isPrimary: boolean;
  onEdit: (provider: AuthProviderConfig) => void;
  onDelete: (provider: AuthProviderConfig) => void;
  onToggle: (providerType: string, enabled: boolean) => void;
  onSetPrimary: (providerType: string) => void;
  loading: boolean;
}

/**
 * Renders a single authentication provider in the list
 */
export const ProviderListItem: React.FC<ProviderListItemProps> = ({
  provider,
  template,
  isPrimary,
  onEdit,
  onDelete,
  onToggle,
  onSetPrimary,
  loading,
}) => {
  const { t } = useTranslation();

  const canDisable = !(
    provider.provider_type === 'local' &&
    provider.is_enabled &&
    isPrimary
  );

  return (
    <ListItem>
      <ListItemIcon>{template.icon}</ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1">{template.name}</Typography>
            {isPrimary && (
              <Chip
                label={t('auth.providers.chips.primary')}
                color="primary"
                size="small"
              />
            )}
            {provider.config?.sync_groups && (
              <Chip
                label={t('auth.providers.chips.groupSync')}
                color="info"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        }
        slotProps={{ secondary: { component: 'div' } }}
        secondary={
          <Box>
            <Typography variant="body2" color="text.secondary" component="span">
              {template.description}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              {provider.config?.tenant_id && (
                <Typography variant="caption" color="text.secondary" component="span">
                  {t('auth.providers.fields.tenant')}: {provider.config.tenant_id}
                </Typography>
              )}
              {provider.config?.client_id && (
                <Typography variant="caption" color="text.secondary" component="span">
                  {t('auth.providers.fields.client')}: {provider.config.client_id.substring(0, 8)}
                  ...
                </Typography>
              )}
              {provider.config?.domain && (
                <Typography variant="caption" color="text.secondary" component="span">
                  {t('auth.providers.fields.domain')}: {provider.config.domain}
                </Typography>
              )}
            </Box>
          </Box>
        }
      />
      <ListItemSecondaryAction>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant={provider.is_enabled ? 'outlined' : 'contained'}
            color={provider.is_enabled ? 'error' : 'success'}
            onClick={() => onToggle(provider.provider_type, !provider.is_enabled)}
            disabled={loading || !canDisable}
            sx={{ mr: 1 }}
          >
            {provider.is_enabled
              ? t('auth.providers.disable')
              : t('auth.providers.enable')}
          </Button>
          {provider.is_enabled && !isPrimary && (
            <Button
              size="small"
              onClick={() => onSetPrimary(provider.provider_type)}
              disabled={loading}
            >
              {t('auth.providers.setPrimary')}
            </Button>
          )}
          <IconButton edge="end" onClick={() => onEdit(provider)} disabled={loading}>
            <EditIcon />
          </IconButton>
          {provider.provider_type !== 'local' && (
            <IconButton
              edge="end"
              onClick={() => onDelete(provider)}
              color="error"
              disabled={loading}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      </ListItemSecondaryAction>
    </ListItem>
  );
};
